export type UnitConfiguration = {
    symbol: string;
    mathjs: string;
};

// Units or "UnitKeys" for available units in the system
export type Unit =
    | "kilopascal"
    | "bar"
    | "psi"
    | "liters_per_minute"
    | "gallons_per_minute"
    | "liters_per_hour"
    | "gallons_per_hour"
    | "pulse_per_minute"
    | "hertz"
    | "liter"
    | "gallon"
    | "millimeter"
    | "micrometer"
    | "centimeter"
    | "meter"
    | "inch"
    | "foot"
    | "celsius"
    | "fahrenheit"
    | "watt_per_square_meter"
    | "microsiemens_per_cm"
    | "deciSiemens_per_meter"
    | "percent"
    | "percent_rh"
    | "volt"
    | "pulse"
    | "meters_per_second"
    | "kilometer_per_hour"
    | "miles_per_hour"
    | "degree"
    | "percent_vic"
    | "liters_per_pulse"
    | "gallons_per_pulse"
    | "kilopascal_per_volt"
    | "minutes"
    | "milliseconds"
    | "seconds"
    | "dimensionless"
    | "ndvi";

/**
 * Unit definitions
 */
export const UnitConfigurations: Record<Unit, UnitConfiguration> = {
    // Pressure
    kilopascal: { symbol: "kPa", mathjs: "kPa" },
    bar: { symbol: "bar", mathjs: "bar" },
    psi: { symbol: "psi", mathjs: "psi" },

    // Flow
    liters_per_minute: { symbol: "LPM", mathjs: "l/min" },
    gallons_per_minute: { symbol: "GPM", mathjs: "gal/min" },
    liters_per_hour: { symbol: "LPH", mathjs: "l/h" },
    gallons_per_hour: { symbol: "GPH", mathjs: "gal/h" },

    // Frequency
    pulse_per_minute: { symbol: "pulse/min", mathjs: "pulse/min" },
    hertz: { symbol: "Hz", mathjs: "Hz" },

    // Volume
    liter: { symbol: "L", mathjs: "l" },
    gallon: { symbol: "gal", mathjs: "gal" },

    // Length
    millimeter: { symbol: "mm", mathjs: "mm" },
    micrometer: { symbol: "µm", mathjs: "um" },
    meter: { symbol: "m", mathjs: "m" },
    inch: { symbol: "in", mathjs: "in" },
    foot: { symbol: "ft", mathjs: "ft" },
    centimeter: { symbol: "cm", mathjs: "cm" },

    // Temperature
    celsius: { symbol: "°C", mathjs: "degC" },
    fahrenheit: { symbol: "°F", mathjs: "degF" },

    // Solar radiation
    watt_per_square_meter: { symbol: "W/m²", mathjs: "W/(m^2)" },

    // EC
    microsiemens_per_cm: {
        symbol: "µS/cm",
        mathjs: "uS/cm",
    },
    deciSiemens_per_meter: { symbol: "dS/m", mathjs: "dS/m" },

    // Percent + RH
    percent: { symbol: "%", mathjs: "percent" },
    percent_rh: { symbol: "%RH", mathjs: "percentRH" },

    // Voltage
    volt: { symbol: "V", mathjs: "V" },

    // Count
    pulse: { symbol: "pulse", mathjs: "pulse" },

    //Speed
    meters_per_second: {
        symbol: "m/s",
        mathjs: "m/s",
    },

    kilometer_per_hour: {
        symbol: "km/h",
        mathjs: "km/h",
    },

    miles_per_hour: {
        symbol: "mph",
        mathjs: "mi/h",
    },

    // Direction / heading
    degree: {
        symbol: "°",
        mathjs: "deg",
    },

    // Soil Volumetric Ion Content
    percent_vic: { symbol: "%Vic", mathjs: "percentVic" },

    // Sensor parameters — K Factor
    liters_per_pulse: { symbol: "L/pulse", mathjs: "l/pulse" },
    gallons_per_pulse: { symbol: "gal/pulse", mathjs: "gal/pulse" },

    // Sensor parameters — Pressure transducer slope
    kilopascal_per_volt: { symbol: "kPa/V", mathjs: "kPa/V" },

    // Time
    minutes: { symbol: "min", mathjs: "min" },
    seconds: { symbol: "s", mathjs: "s" },
    milliseconds: { symbol: "ms", mathjs: "ms" },

    // Dimensionless / no unit
    dimensionless: { symbol: "", mathjs: "dimensionless" },

    // NDVI (Normalized Difference Vegetation Index)
    ndvi: { symbol: "NDVI", mathjs: "NDVI" },
} as const satisfies Record<Unit, UnitConfiguration>;
